import { useTranslation } from "react-i18next";
import { CircleCheck as CheckCircleIcon } from "lucide-react";
import type { WhatsAppPhoneNumberCandidate } from "@multi-chat/shared";
import { Button } from "../../../components/ui/button/index.js";
import { cn } from "../../../lib/utils.js";

interface PhoneSelectionProps {
  candidates: WhatsAppPhoneNumberCandidate[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  submitDisabled?: boolean;
  busy: boolean;
}

export function PhoneSelection(props: PhoneSelectionProps): React.JSX.Element {
  const { t } = useTranslation();
  const { candidates, selectedId, onSelect, onSubmit, onCancel, busy, submitDisabled = false } =
    props;

  return (
    <div className="grid gap-4 overflow-hidden rounded-lg border border-line bg-surface p-5">
      <div className="flex items-center gap-2 rounded-md bg-success-muted px-4 py-2 text-[0.9rem] font-medium text-success">
        <CheckCircleIcon size={18} />
        {t("whatsapp.phoneSelection.authorized")}
      </div>
      <h3 className="m-0 text-[1.05rem] font-semibold tracking-[-0.01em]">{t("whatsapp.phoneSelection.title")}</h3>
      <p className="m-0 leading-normal text-muted">
        {t("whatsapp.phoneSelection.subtitleSingle")}
      </p>
      {candidates.length === 0 ? (
        <p className="m-0 text-subtle">{t("whatsapp.phoneSelection.empty")}</p>
      ) : (
        <ul className="m-0 grid list-none gap-3 p-0" role="radiogroup" aria-label={t("whatsapp.phoneSelection.title")}>
          {candidates.map((phone) => {
            const checked = selectedId === phone.externalPhoneNumberId;
            return (
              <li
                key={phone.externalPhoneNumberId}
                className={cn(
                  "cursor-pointer rounded-md border border-line bg-surface px-4 py-3 transition-[border-color,background-color] duration-150 ease-in-out hover:bg-surface-hover",
                  checked &&
                    "border-whatsapp bg-whatsapp-muted hover:border-whatsapp hover:bg-whatsapp-muted",
                )}
              >
                <label className="flex cursor-pointer items-center gap-3">
                  <input
                    type="radio"
                    name="whatsapp-phone-selection"
                    className="size-5 cursor-pointer accent-whatsapp"
                    checked={checked}
                    onChange={() => onSelect(phone.externalPhoneNumberId)}
                    disabled={busy}
                  />
                  <span
                    dir="ltr"
                    className="text-base font-semibold tabular-nums wrap-anywhere [unicode-bidi:isolate]"
                  >
                    {phone.displayPhoneNumber}
                  </span>
                  {phone.verifiedName ? (
                    <span className="text-[0.9rem] text-muted">{phone.verifiedName}</span>
                  ) : null}
                </label>
              </li>
            );
          })}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-4">
        <Button
          variant="whatsapp"
          onClick={onSubmit}
          disabled={busy || submitDisabled || selectedId === null}
        >
          {busy ? t("whatsapp.phoneSelection.saving") : t("whatsapp.phoneSelection.confirm")}
        </Button>
        <Button variant="link" linkDestructive onClick={onCancel} disabled={busy}>
          {t("whatsapp.phoneSelection.cancel")}
        </Button>
      </div>
    </div>
  );
}
